import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChatCircle, Envelope, PhoneTransfer, WebhooksLogo, Notepad, Hash, Wrench } from '@phosphor-icons/react';
import { createPortal } from 'react-dom';

const SimpleActionToolModal = ({ isOpen, onClose, onToolSelect, isInline = false }) => {
  const popupRef = useRef(null);

  // Handle click outside to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }),
    exit: { 
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 }
    }
  };

  // Tool types with clean flat colors (no gradients)
  const toolTypes = [
    {
      id: 'note',
      title: 'Take Note',
      description: 'Record information by filling out predefined fields',
      icon: Notepad,
      color: 'bg-yellow-100 text-yellow-700'
    },
    {
      id: 'transfer',
      title: 'Transfer Call',
      description: 'Forward the call to another number when triggered',
      icon: PhoneTransfer,
      color: 'bg-green-100 text-green-700'
    },
    {
      id: 'sms',
      title: 'Text Message',
      description: 'Send an SMS message when this tool is triggered',
      icon: ChatCircle,
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'email',
      title: 'Email',
      description: 'Send an email when this tool is triggered',
      icon: Envelope,
      color: 'bg-indigo-100 text-indigo-700'
    },
    {
      id: 'api',
      title: 'API',
      description: 'Call an external API endpoint when this tool is triggered',
      icon: WebhooksLogo,
      color: 'bg-purple-100 text-purple-700'
    },
    {
      id: 'dtmf',
      title: 'DTMF',
      description: 'Press numbers on the phone keypad when triggered',
      icon: Hash,
      color: 'bg-orange-100 text-orange-700'
    }
  ];

  const handleToolClick = (toolType) => {
    if (onToolSelect) {
      onToolSelect(toolType);
    }
    onClose();
  };

  // Inline mode - render as a simple overlay
  if (isInline) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={popupVariants}
                         className="absolute inset-0 bg-white/95 backdrop-blur-sm z-[100] overflow-y-auto"
          >
            <div className="p-4 sm:p-6">
              {/* Header */}
              <motion.div 
                className="flex items-center justify-between mb-4 sm:mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                    <Wrench size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
                  </div>
                  <div>
                    <h2 className="font-medium text-base sm:text-lg text-gray-900">Select Tool Type</h2>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                      Choose the type of action for this tool to perform when triggered
                    </p>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <X size={16} className="sm:w-[18px] sm:h-[18px]" />
                </motion.button>
              </motion.div>

              {/* Tool Options */}
              <motion.div 
                className="grid gap-3"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.07
                    }
                  }
                }}
              >
                <AnimatePresence>
                {toolTypes.map((tool, index) => (
                  <motion.button
                    key={tool.id}
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                      exit="exit"
                    whileHover={{ 
                        scale: 1.01,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
                    }}
                    whileTap={{ scale: 0.98 }}
                      className="flex items-center p-3 sm:p-4 border border-gray-100 rounded-xl hover:border-gray-200 hover:bg-gray-50 text-left transition-colors"
                    onClick={() => handleToolClick(tool.id)}
                  >
                      <div className={`p-2 rounded-lg ${tool.color} mr-3 flex-shrink-0`}>
                        <tool.icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </div>
                      <div>
                        <h3 className="font-medium text-gray-900 text-xs sm:text-sm">{tool.title}</h3>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{tool.description}</p>
                    </div>
                  </motion.button>
                ))}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Modal mode - render as portal with backdrop
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
            onClick={onClose}
        >
            <motion.div
            ref={popupRef}
              variants={popupVariants}
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
            onClick={e => e.stopPropagation()}
            >
            {/* Header */}
              <motion.div 
              className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white z-10"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                  <Wrench size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
                  </div>
                <div>
                  <h2 className="font-medium text-base sm:text-lg text-gray-900">Select Tool Type</h2>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                    Choose the type of action for this tool to perform when triggered
                  </p>
                </div>
              </div>

                <motion.button
                whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                aria-label="Close"
                >
                <X size={16} className="sm:w-[18px] sm:h-[18px]" />
                </motion.button>
              </motion.div>

              {/* Content */}
            <div className="p-5 sm:p-7 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
                <motion.div 
                  className="grid gap-3"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: {
                      transition: {
                      staggerChildren: 0.07
                    }
                    }
                  }}
                >
                <AnimatePresence>
                  {toolTypes.map((tool, index) => (
                    <motion.button
                      key={tool.id}
                      custom={index}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      whileHover={{ 
                        scale: 1.01,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
                      }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center p-3 sm:p-4 border border-gray-100 rounded-xl hover:border-gray-200 hover:bg-gray-50 text-left transition-colors"
                      onClick={() => handleToolClick(tool.id)}
                    >
                      <div className={`p-2 rounded-lg ${tool.color} mr-3 flex-shrink-0`}>
                        <tool.icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 text-xs sm:text-sm">{tool.title}</h3>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{tool.description}</p>
                      </div>
                    </motion.button>
                  ))}
                </AnimatePresence>
              </motion.div>
            </div>
              </motion.div>
            </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default SimpleActionToolModal; 